import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import type { Project, ProjectStatus, Score } from "../lib/types";
import { type StatusTone } from "./primitives";

export const PROJECT_STATUS_TONE: Record<ProjectStatus, StatusTone> = {
  planned: "neutral",
  ongoing: "warning",
  completed: "good",
  failed: "critical",
};

/**
 * Scores run 1 (excellent) to 6 (bad) — the opposite direction to most rating
 * UIs, so the label is always shown beside the number and colour never carries
 * the grade alone.
 */
export function scoreTone(score: Score): string {
  if (score <= 2) return "text-success-text";
  if (score <= 4) return "text-ink";
  return "text-critical-text";
}

/**
 * Scoring lives wherever a person has just formed an opinion — on the Projects
 * list, and on the run page right after reading the report. Making someone
 * navigate elsewhere to record a judgement is how judgements go unrecorded.
 *
 * Only completed projects can be scored. The control says why rather than being
 * silently inert: a disabled input with no explanation is a bad way to learn a
 * rule.
 */
export function ScoreControl({
  project,
  onSaved,
  compact = false,
}: {
  project: Project;
  onSaved?: () => void;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });

  const mutation = useMutation({
    mutationFn: (score: Score | null) => api.scoreProject(project.id, score),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project-summary"] });
      queryClient.invalidateQueries({ queryKey: ["run"] });
      onSaved?.();
    },
  });

  if (project.status !== "completed") {
    return (
      <span className="text-xs text-muted">
        {project.status === "failed"
          ? "failed runs are not scored"
          : "scored once completed"}
      </span>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className="field w-auto py-1 text-sm"
        value={project.score ?? ""}
        disabled={mutation.isPending}
        onChange={(e) =>
          mutation.mutate(e.target.value ? (Number(e.target.value) as Score) : null)
        }
        aria-label={`Score for ${project.name}`}
      >
        <option value="">Not scored</option>
        {(options.data?.scores ?? []).map((s) => (
          <option key={s.value} value={s.value}>
            {s.value} — {s.label}
          </option>
        ))}
      </select>

      {project.score !== null ? (
        <span className={`text-xs font-medium ${scoreTone(project.score)}`}>
          {project.score_label}
        </span>
      ) : (
        !compact && (
          <span className="text-xs text-muted">1 excellent · 6 bad</span>
        )
      )}

      {mutation.isError && (
        <span className="text-xs text-critical-text">
          {(mutation.error as Error).message}
        </span>
      )}
    </div>
  );
}
